import { useCallback } from 'react'
import tw from 'twin.macro'
import { graphql } from 'gatsby'

import PricingCard from '@/components/ui/pricing-card'
import RichText from '@/components/ui/rich-text'
import { ExperienceMapper } from '@ninetailed/experience.js-utils'
import { Experience } from '@ninetailed/experience.js-gatsby'

export const fragment = graphql`
  fragment SectionSetOfPricing on ContentfulSectionSetOfPricing {
    __typename
    contentful_id
    headline
    body {
      raw
      references {
        ...Link
      }
    }
    pricingCards {
      ...ComponentPricing
    }
    bottomRichText {
      raw
      #  references {
      #    ...Link
      #  }
    }
    bottomCallout {
      raw
      #  references {
      #    ...Link
      #  }
    }
    highlightTerms {
      term: key
      definition: value
    }
  }
`

export default function SetOfPricing(props): JSX.Element {
  const mapExperiences = useCallback((experiences) => {
    if (!experiences || experiences.length === 0 || !experiences[0]?.variants) return []
    return experiences
      .map(({ variants, ...rest }) => ({
        ...rest,
        variants: variants.map((v) => ({ ...v, id: v.contentful_id })),
      }))
      .filter(ExperienceMapper.isExperienceEntry)
      .map(ExperienceMapper.mapExperience)
  }, [])

  return (
    <Experience
      {...props}
      id={props.contentful_id ?? props.id}
      key={props.contentful_id ?? props.id}
      experiences={mapExperiences(props.nt_experiences)}
      component={SetOfPricingComponent}
      passthroughProps={{ ...props }}
    />
  )
}

export function SetOfPricingComponent({
  headline,
  body,
  hideHeadline,
  pricingCards,
  bottomRichText,
  bottomCallout,
  insideTabs,
  highlightTerms,
}): JSX.Element {
  return (
    <section tw="lg:px-9  max-w-8xl mx-auto" css={[!insideTabs && tw`mb-10 lg:mb-20`]}>
      <div tw="bg-primaryBlue lg:rounded-4xl" css={[insideTabs ? tw`py-8` : tw`px-5 py-8 lg:(px-9 py-16)`]}>
        <div tw="text-white mb-12">
          {headline && !hideHeadline && <h3 tw="text-center">{headline}</h3>}
          {body && (
            <div tw="text-center max-w-3xl mx-auto">
              <RichText body={body} />
            </div>
          )}
        </div>
        {pricingCards && (
          <div
            tw="xl:grid auto-cols-min mx-auto gap-6"
            css={[
              pricingCards.length === 1 && tw`max-w-4xl grid-cols-1`,
              pricingCards.length === 2 && tw`max-w-4xl xl:max-w-6xl grid-cols-2`,
              pricingCards.length === 3 && tw`max-w-4xl xl:max-w-6xl grid-cols-3`,
              pricingCards.length === 4 && tw`grid-cols-4 max-w-8xl`,
            ]}
          >
            {pricingCards.map(({ columnHeading, columnHeadingColumnSpan }, idx) =>
              columnHeading ? (
                <div
                  tw="hidden xl:block relative px-8 pb-[10px] text-2xl text-white text-center after:(content block h-[15px] mb-1 border-r-2 border-l-2 border-t-2 border-t-white rounded-tl-full rounded-tr-full absolute -bottom-[15px] left-9 right-9 )"
                  css={[
                    columnHeadingColumnSpan === '2' && tw`col-span-2`,
                    columnHeadingColumnSpan === '3' && tw`col-span-3`,
                    columnHeadingColumnSpan === '4' && tw`col-span-4`,
                  ]}
                  key={`col-header-${idx}`}
                >
                  {columnHeading}
                </div>
              ) : null
            )}

            {pricingCards.map((pricingCard, idx) => (
              <PricingCard key={`card-${idx}`} {...pricingCard} highlightTerms={highlightTerms} />
            ))}
          </div>
        )}
        <div tw="[p]:(text-white text-sm) max-w-2xl mx-auto text-center my-5" css={bottomCallout && tw`mb-8`}>
          <RichText body={bottomRichText} />
        </div>
        {bottomCallout && (
          <div tw="rounded-4xl max-w-6xl m-auto text-center p-8 bg-white [p]:m-0">
            <RichText body={bottomCallout} />
          </div>
        )}
      </div>
    </section>
  )
}
